import { useTranslation } from "react-i18next";

import { useProfile } from "@/entities/profile";
import { useUpdateProfile } from "@/features/profile-settings";
import { useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Уведомления. Figma: `Profile · Notification Settings` (26:113).
 *
 * В макете переключателей несколько, в API — один флаг
 * `notifications_enabled`: пуши шлёт бот, и делить их по типам сервер не
 * умеет. Рисовать выключатели, которые ничего не меняют, нельзя.
 */
export function ProfileNotificationsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isPending, isError, refetch } = useProfile();
  const update = useUpdateProfile();

  if (isPending) return <ScreenSkeleton blocks={[88, 72]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const enabled = data.user.notifications_enabled;

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("profile.menu.notifications")} subtitle={t("profile.notifications.subtitle")} />

      <label
        data-haptic="select"
        className="press-row flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4"
      >
        <span className="flex flex-col gap-0.5">
          <span className="text-label font-semibold text-text-primary">
            {t("profile.notifications.label")}
          </span>
          <span className="text-caption font-normal text-text-secondary">
            {t("profile.notifications.hint")}
          </span>
        </span>
        <input
          type="checkbox"
          role="switch"
          checked={enabled}
          disabled={update.isPending}
          onChange={(event) => {
            update.mutate(
              { notifications_enabled: event.target.checked },
              {
                onError: () => {
                  toast.show({ tone: "loss", title: t("states.error.title") });
                },
              },
            );
          }}
          className="size-6 shrink-0 accent-action-primary"
        />
      </label>
    </div>
  );
}
